import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Eye, EyeOff, Lock } from "lucide-react";
import { BRAND_ASSETS } from "@/lib/brandAssets";

const LOGO_URL = BRAND_ASSETS.logo;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const { login, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) { navigate("/admin"); return; }
    // Check if admin exists — if not, redirect to setup
    axios.get(`${API}/admin/status`).then(r => {
      if (!r.data.exists) navigate("/admin/setup");
      setChecking(false);
    }).catch(() => setChecking(false));
  }, [token, navigate]);

  if (token || checking) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter both email and password"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      login(res.data.access_token, res.data.admin);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-4" data-testid="admin-login-page">
      <Card className="w-full max-w-md border border-gray-800 shadow-2xl bg-[#1a1a1a]">
        <CardContent className="p-6 sm:p-8">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Junk In Da Truck" className="h-20 w-auto mx-auto mb-4 rounded-xl" loading="lazy" />
            <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Admin Login</h1>
            <p className="text-sm text-gray-400 mt-1">Management Portal</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-300">Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@junkindatruck.com" data-testid="admin-login-email-input" autoComplete="email" className="bg-[#0D0D0D] border-gray-700 text-white placeholder:text-gray-500" />
            </div>
            <div>
              <Label className="text-gray-300">Password</Label>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" data-testid="admin-login-password-input" autoComplete="current-password" className="bg-[#0D0D0D] border-gray-700 text-white placeholder:text-gray-500" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] font-semibold gap-2" disabled={loading} data-testid="admin-login-submit-button">
              {loading ? "Signing in..." : "Sign In"}
              {!loading && <Lock className="w-4 h-4" />}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <a href={`${API}/auth/forgot-password`} className="text-sm text-[#97BF04] hover:underline">Forgot password?</a>
          </div>
          <div className="mt-6 text-center">
            <a href="/" className="text-xs text-gray-500 hover:text-[#97BF04]">&larr; Back to website</a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
